import { Controller } from "@hotwired/stimulus"

// Generates Apple Passwords-style strong passwords:
// Format: xxxxxx-xxxxxx-xxxxxx (20 chars total)
// - 3 groups of 6 lowercase letters (CVC-CVC syllable pattern per group)
// - Separated by 2 hyphens
// - 1 digit replacing a letter (5 possible positions: either side of each hyphen, or end)
// - 1 uppercase replacing a letter (any non-digit letter position)
// Result: ~71 bits of entropy
export default class extends Controller {
  static targets = ["field"]

  generate() {
    this.fieldTarget.value = this.#generatePassword()
    this.fieldTarget.type = "text"
    this.fieldTarget.focus()
  }

  #generatePassword() {
    const consonants = "bcdfghjklmnpqrstvwx"
    const vowels = "aeiouy"

    // Build three 6-char groups using CVC-CVC syllable pattern
    const groups = [0, 1, 2].map(() => this.#syllablePair(consonants, vowels))

    // Flatten to array of [groupIndex, charIndex] positions for all 18 letter slots
    const allPositions = groups.flatMap((g, gi) =>
      g.split("").map((_, ci) => ({ gi, ci }))
    )

    // Pick digit position: adjacent to a hyphen (positions 5,6,11,12) or end (17)
    const digitCandidates = [5, 6, 11, 12, 17]
    const digitPos = this.#sample(digitCandidates)

    // Pick uppercase position: any letter position that isn't the digit position
    const letterPositions = allPositions
      .map((p, i) => ({ ...p, flat: i }))
      .filter(p => p.flat !== digitPos)
    const upperEntry = this.#sample(letterPositions)

    // Build the final characters
    const chars = groups.map(g => g.split(""))

    // Apply digit
    const dGi = Math.floor(digitPos / 6)
    const dCi = digitPos % 6
    chars[dGi][dCi] = String(this.#randomInt(0, 9))

    // Apply uppercase
    chars[upperEntry.gi][upperEntry.ci] = chars[upperEntry.gi][upperEntry.ci].toUpperCase()

    return chars.map(g => g.join("")).join("-")
  }

  #syllablePair(consonants, vowels) {
    // CVC + CVC = 6 characters
    return (
      this.#pick(consonants) +
      this.#pick(vowels) +
      this.#pick(consonants) +
      this.#pick(consonants) +
      this.#pick(vowels) +
      this.#pick(consonants)
    )
  }

  #pick(str) {
    return str[this.#randomInt(0, str.length - 1)]
  }

  #sample(arr) {
    return arr[this.#randomInt(0, arr.length - 1)]
  }

  #randomInt(min, max) {
    const range = max - min + 1
    const bytes = new Uint32Array(1)
    crypto.getRandomValues(bytes)
    return min + (bytes[0] % range)
  }
}
